import { createElement, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Global, css } from '@emotion/react';
import { Dropdown } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

import DownSvg from 'assets/svg/Down';
import { type AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { type KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import {
    createDefaultKpiAxisConfiguration,
    type KpiDataRange,
    type KpiBinLabelAffixes,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import AxisConfigDropdownPanel from 'components/molecules/PackArchitecture/AxisConfigDropdownPanel';
import AxisConfigSettingsModals from 'components/molecules/PackArchitecture/AxisConfigSettingsModals';
import { colorConstants } from 'config/colorConstants';
import { getKpiAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';

const AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS = 'pack-architecture-axis-config-dropdown';

const axisConfigDropdownGlobalStyles = css`
    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-dropdown-menu {
        padding: 0;
        background: transparent;
        box-shadow: none;
        border-radius: 0;
    }

    .pack-architecture-combined-bubble-config .ant-dropdown-trigger {
        display: flex;
        height: 32px;
        min-width: 0;
    }

    .pack-architecture-combined-bubble-config .ant-dropdown-trigger:last-of-type {
        width: auto;
        flex-shrink: 0;
    }
`;

const AxisConfigDropdownGlobalStyles: FC = () =>
    createElement(Global, { styles: axisConfigDropdownGlobalStyles });

export type AxisOption = {
    label: string;
    value: string;
};

export type AxisOptionGroup = {
    title: string;
    options: AxisOption[];
};

const isDimensionAxisValue = (
    axisValue: string,
    groups: AxisOptionGroup[],
    kpiColumnOptionIds: string[],
) =>
    !kpiColumnOptionIds.includes(axisValue) &&
    groups.some((group) => group.options.some((option) => option.value === axisValue));

const isKpiAxisValue = (axisValue: string, kpiColumnOptionIds: string[]) =>
    kpiColumnOptionIds.includes(axisValue);

const getOptionLabel = (value: string, groups: AxisOptionGroup[]) => {
    for (const group of groups) {
        const match = group.options.find((option) => option.value === value);
        if (match) {
            return match.label;
        }
    }
    return value;
};

type AxisConfigDropdownProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    optionGroups?: AxisOptionGroup[];
    kpiColumnOptionIds?: string[];
    /** When true, renders without outer border — for use inside a combined toolbar group. */
    embedded?: boolean;
    showSettings?: boolean;
    groupByControlsMeta?: AxisGroupByControlsMeta | null;
    kpiControlsMeta?: KpiAxisControlsMeta | null;
    chartRequest?: PricePackArchitectureRequest | null;
    apiTotalCount?: number;
    kpiDataRange?: KpiDataRange;
    kpiBinLabelAffixes?: KpiBinLabelAffixes;
    axisConfiguration?: AxisConfiguration;
    onAxisConfigurationChange?: (config: AxisConfiguration) => void;
    kpiAxisConfiguration?: KpiAxisConfiguration;
    onKpiAxisConfigurationChange?: (config: KpiAxisConfiguration) => void;
};

const AxisConfigDropdown = ({
    label,
    value,
    onChange,
    optionGroups = [],
    kpiColumnOptionIds = [],
    embedded = false,
    showSettings = false,
    groupByControlsMeta = null,
    kpiControlsMeta = null,
    chartRequest = null,
    apiTotalCount = 0,
    kpiDataRange,
    kpiBinLabelAffixes,
    axisConfiguration,
    onAxisConfigurationChange,
    kpiAxisConfiguration,
    onKpiAxisConfigurationChange,
}: AxisConfigDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [draftValue, setDraftValue] = useState(value);
    const [localAxisConfiguration, setLocalAxisConfiguration] = useState<AxisConfiguration | undefined>();
    const [localKpiAxisConfiguration, setLocalKpiAxisConfiguration] = useState<
        KpiAxisConfiguration | undefined
    >();

    const resolvedAxisConfiguration = axisConfiguration ?? localAxisConfiguration;
    const resolvedKpiAxisConfigurationState = kpiAxisConfiguration ?? localKpiAxisConfiguration;

    const selectedLabel = useMemo(() => getOptionLabel(value, optionGroups), [optionGroups, value]);
    const isDimensionAxis = useMemo(
        () => isDimensionAxisValue(value, optionGroups, kpiColumnOptionIds),
        [kpiColumnOptionIds, optionGroups, value],
    );
    const isKpiAxis = useMemo(
        () => isKpiAxisValue(value, kpiColumnOptionIds),
        [kpiColumnOptionIds, value],
    );
    const showAxisSettings = showSettings && (isDimensionAxis || isKpiAxis);
    const kpiConfigurationDefaults = useMemo(
        () => getKpiAxisConfigurationDefaultsFromMeta(kpiControlsMeta),
        [kpiControlsMeta],
    );
    const resolvedKpiAxisConfiguration = useMemo(
        () =>
            resolvedKpiAxisConfigurationState ??
            createDefaultKpiAxisConfiguration(kpiDataRange ?? DEFAULT_KPI_DATA_RANGE, kpiConfigurationDefaults),
        [kpiConfigurationDefaults, kpiDataRange, resolvedKpiAxisConfigurationState],
    );

    const handleApplyAxisConfiguration = (config: AxisConfiguration) => {
        if (onAxisConfigurationChange) {
            onAxisConfigurationChange(config);
        } else {
            setLocalAxisConfiguration(config);
        }
    };

    const handleApplyKpiAxisConfiguration = (config: KpiAxisConfiguration) => {
        if (onKpiAxisConfigurationChange) {
            onKpiAxisConfigurationChange(config);
        } else {
            setLocalKpiAxisConfiguration(config);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setDraftValue(value);
        }
    }, [isOpen, value]);

    const handleApply = () => {
        onChange(draftValue);
        setIsOpen(false);
    };

    const dropdownTrigger = (
        <Dropdown
            trigger={['click']}
            open={isOpen}
            onOpenChange={setIsOpen}
            placement="bottomLeft"
            align={{ offset: [0, 5] }}
            overlayClassName={AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS}
            dropdownRender={() => (
                <AxisConfigDropdownPanel
                    optionGroups={optionGroups}
                    draftValue={draftValue}
                    onDraftChange={setDraftValue}
                    onApply={handleApply}
                />
            )}
        >
            <button
                type="button"
                className={`flex min-w-0 cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left shadow-none outline-none ${
                    embedded ? 'h-8 px-3' : 'h-full min-w-0 flex-1 px-3'
                }`}
            >
                <span className="whitespace-nowrap text-[14px] text-toolbarLabelGrey">{label}:</span>
                <span
                    className="inline-flex min-w-0 items-center gap-2 text-sm font-normal"
                    style={{ color: colorConstants.blackTitle }}
                >
                    <span className="truncate">{selectedLabel}</span>
                    <DownSvg dropDownStroke={colorConstants.headerColor} />
                </span>
            </button>
        </Dropdown>
    );

    const settingsButton = showAxisSettings ? (
        <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center border-l border-borderGreyAl1 text-toolbarLabelGrey"
            aria-label={`${label} settings`}
            onClick={(event) => {
                event.stopPropagation();
                setIsSettingsOpen(true);
            }}
        >
            <SettingOutlined aria-hidden />
        </button>
    ) : null;

    return (
        <>
            <AxisConfigDropdownGlobalStyles />
            {embedded ? (
                dropdownTrigger
            ) : (
                <div className="flex h-8 items-stretch overflow-hidden rounded-md border border-borderGreyAl1">
                    {dropdownTrigger}
                    {settingsButton}
                </div>
            )}
            <AxisConfigSettingsModals
                isDimensionAxis={isDimensionAxis}
                isKpiAxis={isKpiAxis}
                isSettingsOpen={isSettingsOpen}
                selectedLabel={selectedLabel}
                xAxis={value}
                chartRequest={chartRequest}
                apiTotalCount={apiTotalCount}
                groupByControlsMeta={groupByControlsMeta}
                kpiControlsMeta={kpiControlsMeta}
                axisConfiguration={resolvedAxisConfiguration}
                resolvedKpiAxisConfiguration={resolvedKpiAxisConfiguration}
                dataRange={kpiDataRange ?? DEFAULT_KPI_DATA_RANGE}
                kpiBinLabelAffixes={kpiBinLabelAffixes}
                onClose={() => setIsSettingsOpen(false)}
                onApplyAxisConfiguration={handleApplyAxisConfiguration}
                onApplyKpiAxisConfiguration={handleApplyKpiAxisConfiguration}
            />
        </>
    );
};

export default AxisConfigDropdown;
